import React, { PureComponent } from 'react';

import { RouteContext } from 'react-resource-router';
import { Dispatch } from 'redux';

import Spinner from '@atlaskit/spinner';

import { MediaClientConfigProvider } from 'src/contexts/media-client-provider';
import { MediaIdProvider } from 'src/contexts/media-id';
import {
  LOAD_PULL_REQUEST,
  LOAD_SSR_COMMENTS_CONTEXT,
  UNLOAD_PULL_REQUEST,
} from 'src/redux/pull-request/actions';
import { makeBackbucketSafeNow } from 'src/redux/pull-request/sagas/fetch-pull-request-saga';
import { hasTransitioned } from 'src/router/history';
import { RouteComponentProps } from 'src/router/types';
import { Loading } from 'src/sections/global/components/loading.style';
import { publishFact } from 'src/utils/analytics/publish';
import { appWasServerSideRendered } from 'src/utils/ssr';

import PullRequestHelmet from '../containers/pull-request-helmet';
import { PullRequestFact, PullRequestFactPropertiesSchema } from '../facts';

import { ReviewedFilesWrapper } from './reviewed-files/reviewed-files-wrapper';
import { PullRequestDetailsTabbed } from './tabs/pull-request-tabbed';

export type PullRequestPageDispatchProps = {
  onExitPage: () => void;
  onPullRequestOpened: () => void;
  dispatch: Dispatch;
};

export type PullRequestPageStateProps = {
  factProperties: PullRequestFactPropertiesSchema;
  pullRequestStateReady: boolean;
  isPullRequestExists: boolean;
};

// The route component recieves either RouteComponentProps or RouteContext from react-resource-router
export type PullRequestPageOwnProps = RouteComponentProps | RouteContext;

export type PullRequestPageMergeProps = {
  dispatchEnteredCodeReview: () => void;
};

export type PullRequestPageProps = PullRequestPageDispatchProps &
  PullRequestPageStateProps &
  PullRequestPageOwnProps &
  PullRequestPageMergeProps;

class BasePullRequestPage extends PureComponent<PullRequestPageProps> {
  componentDidMount() {
    if (appWasServerSideRendered() && !hasTransitioned()) {
      this.props.dispatch({
        type: LOAD_PULL_REQUEST.SUCCESS,
        payload: { result: { lastPoll: makeBackbucketSafeNow() } },
      });
      this.props.dispatch({
        type: LOAD_SSR_COMMENTS_CONTEXT,
      });
    }
    // Note: Most mounts will require fetching data.  However, PR state will
    // be ready on mount in the event that the user returns to the last loaded PR
    // from another Frontbucket page. The init() correctly handles both cases.
    this.props.dispatchEnteredCodeReview();
    this.init();
  }

  componentDidUpdate() {
    // In current use, component updates only occur after initially fetching data (ie.
    // shortly after initial mount). In future, SPA Pull Request -> Pull Request transitions
    // will also trigger component updates. The init() correctly handles both cases.
    const {
      pullRequestStateReady,
      dispatchEnteredCodeReview,
      isPullRequestExists,
    } = this.props;
    if (isPullRequestExists && !pullRequestStateReady) {
      dispatchEnteredCodeReview();
    }
    this.init();
  }

  componentWillUnmount() {
    this.props.dispatch({ type: UNLOAD_PULL_REQUEST });
    this.props.onExitPage();
  }

  init() {
    const { pullRequestStateReady } = this.props;
    if (pullRequestStateReady) {
      this.publishPullRequestOpenedEvent();
    }
  }

  publishPullRequestOpenedEvent() {
    this.props.onPullRequestOpened();
    publishFact(
      new PullRequestFact(
        'bitbucket.pullrequests.open',
        this.props.factProperties
      )
    );
  }

  render() {
    const { pullRequestStateReady } = this.props;

    return (
      <MediaClientConfigProvider>
        <MediaIdProvider>
          <PullRequestHelmet />
          <ReviewedFilesWrapper>
            {pullRequestStateReady ? (
              <PullRequestDetailsTabbed />
            ) : (
              <Loading>
                <Spinner testId="pr-page-loading-spinner" size="large" />
              </Loading>
            )}
          </ReviewedFilesWrapper>
        </MediaIdProvider>
      </MediaClientConfigProvider>
    );
  }
}

export const PullRequestPage = BasePullRequestPage;
